import React, { FormEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { getLoadingState } from '../../utils/Helper/helper';
import { getAllGitReports, IGitReport } from '../../store/slice/gitReportSlice';
import DatePicker from 'react-datepicker';
import GitTrail from '../../utils/GitTrail/GitTrail';
import ReportCard from '../../components/Cards/ReportCard';
import Button from '../../library/Button/button';
import ConditionalLoader from '../../components/ConditionalLoader';
import { addDays, getDateToday } from '../../utils/Date/transformDate';
import { IGitTrailResponseQueryFilters } from '../../utils/GitTrail/gitTrail.interface';
import ReportGraphs from '../../components/Reports/ReportGraphs';
import SmallLoader from '../../library/Loader/SmallLoader';
import { FaArrowRotateRight } from 'react-icons/fa6';

const Reports: React.FC = () => {
  const FaArrowRotateRightIcon = FaArrowRotateRight as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const dispatch = useAppDispatch();
  const gitReportState = useAppSelector((state) => state.gitReport);
  const data = gitReportState.data?.map((report) => report) ?? [];
  const [requestedReport, setRequestedReport] = useState<boolean>(false);
  const [selectedReportIndex, setSelectedReportIndex] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const report = data?.[selectedReportIndex]?.report?.report;
  const selectedReport = data && report ? (JSON.parse(report) as unknown as IGitReport[]) : [];
  const isSinking = gitReportState.data !== null && gitReportState.status === 'loading';

  const handleReportCardClick = (itemInd: number) => {
    if (data && data[itemInd].report.status === 'success') {
      setSelectedReportIndex(itemInd);
    } else {
      void dispatch(getAllGitReports());
    }
  };

  const reportCards = data.map((report, index) => (
    <ReportCard
      key={report.id + Math.random()}
      start={new Date(Number(report.report.start)).toUTCString()}
      end={new Date(Number(report.report.end)).toUTCString()}
      selected={index === selectedReportIndex}
      status={report.report.status}
      onClick={() => {
        handleReportCardClick(index);
      }}
    />
  ));

  const isLoadingReports = getLoadingState(gitReportState);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Submit Handler
  const handleSubmitReportRequest = async (e: FormEvent<Element>) => {
    e.preventDefault();
    if (!startDate) return;
    setRequestedReport(true);
    const start: number = Date.parse(getDateToday(startDate.toUTCString()));
    let end: number = Date.now();
    if (endDate) {
      end = Date.parse(addDays(endDate, 1));
    }
    const filterObject: IGitTrailResponseQueryFilters = {
      start,
      end
    };
    const gitTrail = new GitTrail();
    await gitTrail.requestGitTrailReport(filterObject);
    setRequestedReport(false);
  };

  const handleRefreshReports = () => {
    void dispatch(getAllGitReports());
  };

  useEffect(() => {
    if (gitReportState.status !== 'loading') {
      void dispatch(getAllGitReports());
    }
  }, []);

  return (
    <div className="overflow-y-auto bg-gray rounded-md mt-2 p-4 min-h-[700px]">
      <div>
        <div className="flex justify-end flex-wrap gap-2 pb-2 z-10">
          <DatePicker
            id="start-date-select"
            className="sm:w-52 lg:w-60 bg-right bg-size-32 border border-light-gray px-4 py-2 rounded-md bg-no-repeat overflow-hidden z-10"
            selected={startDate}
            onChange={handleStartDateChange}
            placeholderText="From"
            dateFormat="yyyy-MM-dd"
            maxDate={endDate ? new Date(endDate) : new Date()}
            autoComplete="off"
            required
          />
          <DatePicker
            id="end-date-select"
            className="sm:w-52 lg:w-60 bg-right bg-size-32 border border-light-gray px-4 py-2 rounded-md bg-no-repeat overflow-hidden"
            selected={endDate}
            onChange={handleEndDateChange}
            placeholderText="To"
            dateFormat="yyyy-MM-dd"
            maxDate={new Date()}
            minDate={startDate ? new Date(startDate) : undefined}
            autoComplete="off"
          />
          <Button
            id="submit-filters"
            className="w-32 flex-shrink-0 h-10 px-4 text-center bg-black text-white rounded-md focus:outline-none border border-spacing-1 border-black"
            onclick={handleSubmitReportRequest}
          >
            {requestedReport ? (
              <SmallLoader className="mx-auto" loaderStyle="stroke-white" />
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </div>
      {reportCards.length > 0 ? (
        <div className="w-full overflow-x-auto">
          <ConditionalLoader isLoading={isLoadingReports}>
            <div className="w-fit flex justify-center gap-4 mx-auto">
              <div className="w-fit grow-0 rounded-md">
                <ReportGraphs report={selectedReport} />
              </div>
              <div className="flex flex-col shadow-lg p-4 rounded-md bg-offwhite">
                <div className="flex justify-end">
                  <Button
                    className="bg-white p-2 rounded-lg"
                    onclick={handleRefreshReports}
                    disabled={isSinking}
                  >
                    <FaArrowRotateRightIcon className={isSinking ? 'animate-spin' : ''} />
                  </Button>
                </div>
                {reportCards}
              </div>
            </div>
          </ConditionalLoader>
        </div>
      ) : (
        <p className="text-center text-lg mt-40">Reports not available, Please request one.</p>
      )}
    </div>
  );
};
export default Reports;
